import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { apiService } from '../services/apiService';
import { useUser } from '../contexts/UserContext';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 20px 20px;
  min-height: 100vh;
  background: linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%);
`;

const Header = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  text-align: center;
`;

const HeaderTitle = styled.h1`
  color: #2c3e50;
  font-size: 2.5rem;
  margin-bottom: 10px;
  font-weight: 700;
`;

const HeaderSubtitle = styled.p`
  color: #7f8c8d;
  font-size: 1.2rem;
`;

const StatsOverview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #3F8EF6;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  color: #7f8c8d;
  font-weight: 500;
  font-size: 1.1rem;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  margin-bottom: 20px;
`;

const CardTitle = styled.h2`
  color: #2c3e50;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.8rem;
`;

const TopicSection = styled(motion.div)<{ $expanded: boolean }>`
  margin-bottom: 30px;
`;

const TopicHeader = styled.div<{ $mastered?: boolean }>`
  background: ${props => props.$mastered ? 
    'linear-gradient(135deg, #A0F0D0, #7dd3fc)' : 
    'linear-gradient(135deg, #3F8EF6, #2976d4)'
  };
  color: ${props => props.$mastered ? '#065f46' : 'white'};
  padding: 20px 25px;
  border-radius: 15px 15px 0 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$mastered ? 
      'linear-gradient(135deg, #7dd3fc, #38bdf8)' : 
      'linear-gradient(135deg, #2976d4, #1e5bb8)'
    };
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(63, 142, 246, 0.4);
  }
`;

const TopicInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TopicName = styled.span`
  font-size: 1.3rem;
  font-weight: bold;
`;

const TopicBadge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  transition: transform 0.3s ease;
  font-size: 1.2rem;
  transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const Subtopics = styled(motion.div)<{ $expanded: boolean }>`
  display: ${props => props.$expanded ? 'block' : 'none'};
  background: white;
  border-radius: 0 0 15px 15px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
`;

const SubtopicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

const SubtopicCard = styled(motion.div)`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #3F8EF6;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: #e8f4f8;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
`;

const SubtopicHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const SubtopicName = styled.span`
  font-size: 1.1rem;
  font-weight: bold;
  color: #2c3e50;
`;

const MissedCount = styled.span`
  background: #3F8EF6;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
`;

const SubtopicDescription = styled.div`
  color: #7f8c8d;
  font-size: 0.95rem;
  margin-bottom: 15px;
  line-height: 1.4;
`;

const PracticeButton = styled.button`
  background: linear-gradient(135deg, #A0F0D0, #7dd3fc);
  color: #065f46;
  padding: 12px 25px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(160, 240, 208, 0.4);
    background: linear-gradient(135deg, #7dd3fc, #38bdf8);
  }
`;

const NoMissed = styled.div`
  text-align: center;
  padding: 40px;
  color: #7f8c8d;
`;

const NoMissedIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  color: #A0F0D0;
`;

const NoMissedTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #3F8EF6;
  font-size: 1.2rem;
`;

interface MissedQuestionsStat {
  totalMissed: number;
  topicsWithGaps: number;
  needReview: number;
  overallAccuracy: number;
}

interface TopicData {
  topic: string;
  topicKey: string; // Add database key for the topic
  topicIcon: string;
  missedCount: number;
  subtopics: SubtopicData[];
  mastered: boolean;
}

interface SubtopicData {
  name: string;
  missedCount: number;
  description: string;
  subtopicKey: string;
}

const AMCMissedQuestions: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<MissedQuestionsStat | null>(null);
  const [topicsData, setTopicsData] = useState<TopicData[]>([]);

  useEffect(() => {
    fetchMissedQuestionsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchMissedQuestionsData = async () => {
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.get('/amc/missed-questions');
      
      if (response.success) {
        setStats(response.stats);
        setTopicsData(response.topics);
        
        // Auto-expand topics with missed questions
        const topicsWithMissed = response.topics
          .filter((topic: TopicData) => !topic.mastered)
          .map((topic: TopicData) => topic.topic);
        setExpandedTopics(new Set(topicsWithMissed));
      } else {
        throw new Error(response.message || 'Failed to fetch missed questions data');
      }
    } catch (err) {
      console.error('Error fetching missed questions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load missed questions data');
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = (topic: string) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topic)) {
      newExpanded.delete(topic);
    } else {
      newExpanded.add(topic);
    }
    setExpandedTopics(newExpanded);
  };

  const startPractice = (topicKey: string, subtopic: string) => {
    // Navigate to AMC missed practice with topic and subtopic filters
    navigate(`/amc-missed-practice?topic=${encodeURIComponent(topicKey)}&subtopic=${encodeURIComponent(subtopic)}`);
  };

  if (loading) {
    return (
      <>
        <Container>
          <LoadingContainer>
            <div>Loading missed questions data...</div>
          </LoadingContainer>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Container>
          <Card>
            <CardTitle>❌ Error</CardTitle>
            <p style={{ color: '#e74c3c', marginBottom: '20px' }}>{error}</p>
            <PracticeButton onClick={() => navigate('/amc')}>
              ← Back to Dashboard
            </PracticeButton>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <Container>
        <Header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <HeaderTitle>📊 Missed Questions Dashboard</HeaderTitle>
          <HeaderSubtitle>Track your progress and focus on areas that need improvement</HeaderSubtitle>
        </Header>

        <StatsOverview>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <StatNumber>{stats?.totalMissed || 0}</StatNumber>
            <StatLabel>Total Missed</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <StatNumber>{stats?.topicsWithGaps || 0}</StatNumber>
            <StatLabel>Topics with Gaps</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <StatNumber>{stats?.needReview || 0}</StatNumber>
            <StatLabel>Need Review</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <StatNumber>{stats?.overallAccuracy || 0}%</StatNumber>
            <StatLabel>Overall Accuracy</StatLabel>
          </StatCard>
        </StatsOverview>

        <Card
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <CardTitle>📚 Topics Needing Practice</CardTitle>

          {topicsData.map((topicData, index) => {
            const isExpanded = expandedTopics.has(topicData.topic);
            return (
              <TopicSection
                key={topicData.topic}
                $expanded={isExpanded}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
              >
                <TopicHeader
                  $mastered={topicData.mastered}
                  onClick={() => toggleTopic(topicData.topic)}
                >
                  <TopicInfo>
                    <TopicName>{topicData.topicIcon} {topicData.topic}</TopicName>
                    <TopicBadge>
                      {topicData.mastered ? '0 missed' : `${topicData.missedCount} missed`}
                    </TopicBadge>
                  </TopicInfo>
                  <ExpandIcon $expanded={isExpanded}>▼</ExpandIcon>
                </TopicHeader>

                <Subtopics $expanded={isExpanded}>
                  {topicData.mastered ? (
                    <NoMissed>
                      <NoMissedIcon>🏆</NoMissedIcon>
                      <NoMissedTitle>Great Job!</NoMissedTitle>
                      <p>You've mastered {topicData.topic.toLowerCase()}! Keep up the excellent work!</p>
                    </NoMissed>
                  ) : (
                    <SubtopicGrid>
                      {topicData.subtopics.map((subtopic, subtopicIndex) => (
                        <SubtopicCard
                          key={subtopic.subtopicKey}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: subtopicIndex * 0.1 }}
                          onClick={() => startPractice(topicData.topicKey, subtopic.subtopicKey)}
                        >
                          <SubtopicHeader>
                            <SubtopicName>{subtopic.name}</SubtopicName>
                            <MissedCount>{subtopic.missedCount}</MissedCount>
                          </SubtopicHeader>
                          <SubtopicDescription>
                            {subtopic.description}
                          </SubtopicDescription>
                          <PracticeButton onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            startPractice(topicData.topicKey, subtopic.subtopicKey);
                          }}>
                            🎯 Practice {subtopic.name}
                          </PracticeButton>
                        </SubtopicCard>
                      ))}
                    </SubtopicGrid>
                  )}
                </Subtopics>
              </TopicSection>
            );
          })}
        </Card>
      </Container>
    </>
  );
};

export default AMCMissedQuestions;